import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Megaphone, Plus, Search, Filter, X, Copy, Trash2, Eye,
  Sparkles, Image as ImageIcon,
  Save, Play, Pause, ExternalLink, ChevronLeft,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { ImageSpec } from '@/components/shared/ImageSpec';
import { ImageUploader } from '@/components/shared/ImageUploader';
import {
  useRoadblockerStore, isLive,
  type Roadblocker, type RoadblockerStatus, type RoadblockerAudience,
  type RoadblockerFrequency, type RoadblockerPlacement,
} from '@/store/roadblocker-store';
import { cn } from '@nepal-ecom/ui';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, useConfirm } from '@nepal-ecom/ui';

const STATUS_VARIANT: Record<RoadblockerStatus, 'success' | 'info' | 'warning' | 'secondary'> = {
  active: 'success', scheduled: 'info', paused: 'warning', draft: 'secondary',
};
const STATUS_LABEL: Record<RoadblockerStatus, string> = {
  active: 'Live', scheduled: 'Scheduled', paused: 'Paused', draft: 'Draft',
};
const AUDIENCE_LABEL: Record<RoadblockerAudience, string> = {
  all: 'Everyone', new: 'New customers', returning: 'Returning customers', guests: 'Guests only', members: 'Logged-in members',
};
const FREQUENCY_LABEL: Record<RoadblockerFrequency, string> = {
  once_per_session: 'Once per session', once_per_day: 'Once per day', once_ever: 'Once ever', every_visit: 'Every visit',
};
const PLACEMENT_LABEL: Record<RoadblockerPlacement, string> = {
  modal_center: 'Center modal', modal_corner: 'Corner card', top_banner: 'Top banner', bottom_banner: 'Bottom banner',
};

function emptyDraft(): Omit<Roadblocker, 'id' | 'createdAt' | 'updatedAt' | 'impressions' | 'clicks'> {
  return {
    name: '',
    title: '',
    body: '',
    imageUrl: '',
    ctaLabel: 'Shop now',
    ctaUrl: '/',
    dismissLabel: 'No thanks',
    status: 'draft',
    placement: 'modal_center',
    audience: 'all',
    frequency: 'once_per_session',
    startAt: '',
    endAt: '',
    delaySeconds: 3,
    accentColor: '#D95100',
  };
}

// ─── Live preview ──────────────────────────────────────────────────────────

function ModalPreview({ rb }: { rb: ReturnType<typeof emptyDraft> }) {
  return (
    <div className="relative rounded-2xl overflow-hidden ring-1 ring-slate-200 shadow-card bg-white">
      {rb.imageUrl && (
        <div className="aspect-[1.91/1] bg-slate-100 overflow-hidden">
          <img src={rb.imageUrl} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <button className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/90 hover:bg-white text-slate-600 flex items-center justify-center shadow-sm backdrop-blur" disabled>
        <X className="h-4 w-4" />
      </button>
      <div className="p-5 sm:p-6">
        <h3 className="text-xl font-bold text-slate-900 leading-tight" style={{ color: rb.accentColor || undefined }}>
          {rb.title || 'Your headline goes here'}
        </h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          {rb.body || 'A short message in 1–2 sentences explaining the offer or announcement.'}
        </p>
        <div className="flex items-center gap-2 mt-5">
          <button
            type="button"
            className="flex-1 sm:flex-none px-4 h-10 rounded-lg text-white text-sm font-semibold shadow-sm transition-opacity hover:opacity-90"
            style={{ background: rb.accentColor }}
          >
            {rb.ctaLabel || 'Continue'}
          </button>
          <button type="button" className="px-4 h-10 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">
            {rb.dismissLabel || 'No thanks'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CornerPreview({ rb }: { rb: ReturnType<typeof emptyDraft> }) {
  return (
    <div className="relative rounded-xl ring-1 ring-slate-200 shadow-card bg-white max-w-sm overflow-hidden">
      <div className="flex">
        {rb.imageUrl && (
          <div className="w-24 flex-shrink-0 bg-slate-100">
            <img src={rb.imageUrl} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-3.5 flex-1 min-w-0">
          <h3 className="text-sm font-bold text-slate-900 leading-tight truncate" style={{ color: rb.accentColor || undefined }}>
            {rb.title || 'Your headline'}
          </h3>
          <p className="text-xs text-slate-600 mt-1 line-clamp-2">{rb.body || 'Short message body'}</p>
          <button type="button" className="mt-2 text-xs font-bold inline-flex items-center gap-1" style={{ color: rb.accentColor }}>
            {rb.ctaLabel || 'Continue'} <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function BannerPreview({ rb }: { rb: ReturnType<typeof emptyDraft> }) {
  return (
    <div className="rounded-lg text-white p-3 flex items-center gap-3 shadow-sm" style={{ background: rb.accentColor }}>
      <Sparkles className="h-4 w-4 flex-shrink-0" />
      <p className="text-sm flex-1 min-w-0 truncate">
        <span className="font-bold mr-2">{rb.title || 'Your headline'}</span>
        <span className="opacity-90">{rb.body || 'short message'}</span>
      </p>
      <button type="button" className="text-xs font-bold bg-white/15 hover:bg-white/25 px-2.5 py-1 rounded">{rb.ctaLabel || 'Shop'}</button>
      <X className="h-4 w-4 opacity-75" />
    </div>
  );
}

function Preview({ rb }: { rb: ReturnType<typeof emptyDraft> }) {
  if (rb.placement === 'modal_center') return <ModalPreview rb={rb} />;
  if (rb.placement === 'modal_corner') return <CornerPreview rb={rb} />;
  return <BannerPreview rb={rb} />;
}

// ─── Form ───────────────────────────────────────────────────────────────────

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <label className="text-xs font-semibold text-slate-700">{label}</label>
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Editor({
  initial, onSave, onCancel,
}: {
  initial: ReturnType<typeof emptyDraft>;
  onSave: (draft: ReturnType<typeof emptyDraft>) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const set = <K extends keyof typeof draft>(k: K, v: (typeof draft)[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const save = () => {
    if (!draft.name.trim()) { toast.error('Internal name is required'); return; }
    if (!draft.title.trim()) { toast.error('Title is required'); return; }
    if (!draft.body.trim()) { toast.error('Body is required'); return; }
    onSave(draft);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
      <div className="space-y-5">
        <Card>
          <CardHeader><CardTitle>Content</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Field label="Internal name" hint="Only visible to admins">
              <Input value={draft.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Dashain Sale 2026" />
            </Field>
            <Field label="Title *" hint={`${draft.title.length}/80`}>
              <Input value={draft.title} maxLength={80} onChange={(e) => set('title', e.target.value)} placeholder="🪔 Dashain Mega Sale" />
            </Field>
            <Field label="Body *" hint={`${draft.body.length}/220`}>
              <textarea
                rows={3}
                maxLength={220}
                value={draft.body}
                onChange={(e) => set('body', e.target.value)}
                placeholder="Up to 50% off across handicrafts, fashion and electronics."
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA label">
                <Input value={draft.ctaLabel ?? ''} onChange={(e) => set('ctaLabel', e.target.value)} placeholder="Shop now" />
              </Field>
              <Field label="CTA URL">
                <Input value={draft.ctaUrl ?? ''} onChange={(e) => set('ctaUrl', e.target.value)} placeholder="/sale/dashain" />
              </Field>
            </div>
            <Field label="Dismiss button label">
              <Input value={draft.dismissLabel ?? ''} onChange={(e) => set('dismissLabel', e.target.value)} placeholder="No thanks" />
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ImageIcon className="h-4 w-4 text-cobalt-600" /> Image</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ImageSpec spec="ogImage" variant="card" />
            <ImageUploader
              label="Hero image"
              value={draft.imageUrl ?? ''}
              onChange={(v) => set('imageUrl', v)}
              aspect="aspect-[1.91/1]"
              helperText="1200×630 recommended. JPEG / PNG / WEBP up to 5MB."
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Display rules</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Placement">
                <select
                  value={draft.placement}
                  onChange={(e) => set('placement', e.target.value as RoadblockerPlacement)}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
                >
                  {Object.entries(PLACEMENT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Audience">
                <select
                  value={draft.audience}
                  onChange={(e) => set('audience', e.target.value as RoadblockerAudience)}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
                >
                  {Object.entries(AUDIENCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Frequency">
                <select
                  value={draft.frequency}
                  onChange={(e) => set('frequency', e.target.value as RoadblockerFrequency)}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
                >
                  {Object.entries(FREQUENCY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Show after (seconds)">
                <Input type="number" min={0} max={120} value={draft.delaySeconds} onChange={(e) => set('delaySeconds', Number(e.target.value))} />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Start date">
                <Input type="datetime-local" value={draft.startAt ? draft.startAt.slice(0, 16) : ''} onChange={(e) => set('startAt', e.target.value ? new Date(e.target.value).toISOString() : '')} />
              </Field>
              <Field label="End date">
                <Input type="datetime-local" value={draft.endAt ? draft.endAt.slice(0, 16) : ''} onChange={(e) => set('endAt', e.target.value ? new Date(e.target.value).toISOString() : '')} />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Accent color">
                <div className="flex items-center gap-2">
                  <input type="color" value={draft.accentColor} onChange={(e) => set('accentColor', e.target.value)} className="h-10 w-14 rounded-lg border border-slate-200 cursor-pointer bg-white" />
                  <Input value={draft.accentColor} onChange={(e) => set('accentColor', e.target.value)} className="font-mono text-xs" />
                </div>
              </Field>
              <Field label="Status">
                <select
                  value={draft.status}
                  onChange={(e) => set('status', e.target.value as RoadblockerStatus)}
                  className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
                >
                  {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={save}><Save className="h-4 w-4" /> Save roadblocker</Button>
        </div>
      </div>

      <aside className="space-y-4 lg:sticky lg:top-6 self-start">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2"><Eye className="h-4 w-4 text-primary-600" /> Live preview</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Updates as you type. Shows the {PLACEMENT_LABEL[draft.placement].toLowerCase()} variant.</p>
          </CardHeader>
          <CardContent className="bg-slate-50 rounded-b-2xl">
            <Preview rb={draft} />
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function Roadblockers() {
  const confirm = useConfirm();
  const list = useRoadblockerStore((s) => s.roadblockers);
  const loaded = useRoadblockerStore((s) => s.loaded);
  const loading = useRoadblockerStore((s) => s.loading);
  const load = useRoadblockerStore((s) => s.load);
  const addRb = useRoadblockerStore((s) => s.add);
  const updateRb = useRoadblockerStore((s) => s.update);
  const removeRb = useRoadblockerStore((s) => s.remove);
  const duplicateRb = useRoadblockerStore((s) => s.duplicate);
  const setStatus = useRoadblockerStore((s) => s.setStatus);

  useEffect(() => {
    load().catch((err) => toast.error('Could not load roadblockers', { description: (err as Error).message }));
  }, [load]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | RoadblockerStatus>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const editing = useMemo(
    () => (editingId ? list.find((r) => r.id === editingId) : undefined),
    [editingId, list],
  );

  const filtered = useMemo(() => {
    let arr = list;
    if (statusFilter !== 'all') arr = arr.filter((r) => r.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((r) => r.name.toLowerCase().includes(q) || r.title.toLowerCase().includes(q));
    }
    return arr;
  }, [list, search, statusFilter]);

  const totals = useMemo(() => ({
    live: list.filter((r) => isLive(r)).length,
    impressions: list.reduce((s, r) => s + r.impressions, 0),
    clicks: list.reduce((s, r) => s + r.clicks, 0),
    ctr: (() => {
      const imp = list.reduce((s, r) => s + r.impressions, 0);
      const clk = list.reduce((s, r) => s + r.clicks, 0);
      return imp ? (clk / imp) * 100 : 0;
    })(),
  }), [list]);

  if (creating) {
    return (
      <div className="animate-fade-in space-y-6">
        <button
          type="button"
          onClick={() => setCreating(false)}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
        >
          <ChevronLeft className="h-4 w-4" /> Roadblockers
        </button>
        <PageHeader title="New roadblocker" description="Compose a customer-facing popup. Save as Draft to preview without going live." />
        <Editor
          initial={emptyDraft()}
          onCancel={() => setCreating(false)}
          onSave={async (d) => {
            try {
              const r = await addRb(d);
              toast.success('Roadblocker created', { description: r.title });
              setCreating(false);
            } catch (err) {
              toast.error('Create failed', { description: (err as Error).message });
            }
          }}
        />
      </div>
    );
  }

  if (editing) {
    const { id, createdAt, updatedAt, impressions, clicks, ...rest } = editing;
    return (
      <div className="animate-fade-in space-y-6">
        <button
          type="button"
          onClick={() => setEditingId(null)}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
        >
          <ChevronLeft className="h-4 w-4" /> Roadblockers
        </button>
        <PageHeader title="Edit roadblocker" description={editing.name} />
        <Editor
          initial={rest}
          onCancel={() => setEditingId(null)}
          onSave={async (d) => {
            try {
              await updateRb(id, d);
              toast.success('Roadblocker saved', { description: d.title });
              setEditingId(null);
            } catch (err) {
              toast.error('Save failed', { description: (err as Error).message });
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Roadblockers & Popups"
        description="Schedule customer-facing popups. Edits apply to the storefront immediately."
        actions={<Button onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> New roadblocker</Button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Live now',    value: totals.live.toString(),                    tone: 'bg-emerald-50 text-emerald-700', icon: Play },
          { label: 'Impressions', value: totals.impressions.toLocaleString(),       tone: 'bg-cobalt-50 text-cobalt-700',   icon: Eye },
          { label: 'Clicks',      value: totals.clicks.toLocaleString(),            tone: 'bg-primary-50 text-primary-700', icon: ExternalLink },
          { label: 'CTR',         value: `${totals.ctr.toFixed(1)}%`,               tone: 'bg-amber-50 text-amber-700',     icon: Sparkles },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center mb-2.5', s.tone)}>
                <s.icon className="h-4 w-4" />
              </div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{s.label}</p>
              <p className="text-2xl font-bold text-slate-900 tabular-nums mt-0.5">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mb-5">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or title…" className="pl-9" />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
            >
              <option value="all">All status</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        {!loaded && loading ? (
          <div className="py-12 text-center text-sm text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            title="No roadblockers yet"
            description="Create your first customer-facing popup to promote a sale, announce a launch, or capture leads."
            action={<Button onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> New roadblocker</Button>}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {['Roadblocker', 'Status', 'Placement', 'Audience', 'Frequency', 'Schedule', 'Impressions', 'CTR', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((r) => {
                  const ctr = r.impressions ? (r.clicks / r.impressions) * 100 : 0;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-9 w-9 rounded-md flex-shrink-0 flex items-center justify-center" style={{ background: `${r.accentColor}20`, color: r.accentColor }}>
                            <Megaphone className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <button onClick={() => setEditingId(r.id)} className="text-sm font-semibold text-slate-900 hover:text-primary-700 truncate block max-w-xs text-left">
                              {r.name}
                            </button>
                            <p className="text-xs text-slate-500 truncate max-w-xs">{r.title}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5"><Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABEL[r.status]}</Badge></td>
                      <td className="px-4 py-3.5 text-xs text-slate-700">{PLACEMENT_LABEL[r.placement]}</td>
                      <td className="px-4 py-3.5 text-xs text-slate-700">{AUDIENCE_LABEL[r.audience]}</td>
                      <td className="px-4 py-3.5 text-xs text-slate-700">{FREQUENCY_LABEL[r.frequency]}</td>
                      <td className="px-4 py-3.5 text-xs text-slate-500 whitespace-nowrap">
                        {r.startAt ? new Date(r.startAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short' }) : '—'}
                        {' → '}
                        {r.endAt ? new Date(r.endAt).toLocaleDateString('en-US', { day: '2-digit', month: 'short' }) : '∞'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-slate-700 tabular-nums">{r.impressions.toLocaleString()}</td>
                      <td className="px-4 py-3.5 text-sm font-semibold text-slate-900 tabular-nums">{ctr.toFixed(1)}%</td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-0.5">
                          {r.status === 'active' ? (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600" title="Pause"
                              onClick={async () => { try { await setStatus(r.id, 'paused'); toast.success('Paused'); } catch (e) { toast.error('Failed', { description: (e as Error).message }); } }}>
                              <Pause className="h-3.5 w-3.5" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600" title="Activate"
                              onClick={async () => { try { await setStatus(r.id, 'active'); toast.success('Activated'); } catch (e) { toast.error('Failed', { description: (e as Error).message }); } }}>
                              <Play className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit" onClick={() => setEditingId(r.id)}>
                            <Sparkles className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Duplicate"
                            onClick={async () => { try { await duplicateRb(r.id); toast.success('Duplicated'); } catch (e) { toast.error('Failed', { description: (e as Error).message }); } }}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:bg-red-50" title="Delete"
                            onClick={async () => {
                              if (!(await confirm({
                                title: `Delete "${r.name}"?`,
                                description: 'This roadblocker will be removed permanently.',
                                confirmLabel: 'Delete',
                                tone: 'danger',
                              }))) return;
                              try { await removeRb(r.id); toast.success('Deleted'); }
                              catch (e) { toast.error('Failed', { description: (e as Error).message }); }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
